import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import { LockIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { FeesStepTestIds } from '../../FeesStep.testIds';

import {
  cardStyles,
  columnLabelStyles,
  columnNoteStyles,
  columnsStyles,
  columnStyles,
  headerIconStyles,
  headerStyles,
  headerTitleStyles,
  lockNoteIconStyles,
} from './OriginatorCompCard.styles';

const EMPTY_VALUE = '—';

const OriginatorColumn = ({ label, value, note, isLockedNote }) => (
  <Box sx={columnStyles}>
    <Typography sx={columnLabelStyles} variant="medium12">
      {label}
    </Typography>
    <Typography variant="bold14">{value ?? EMPTY_VALUE}</Typography>
    {!!note && (
      <Box sx={columnNoteStyles}>
        {isLockedNote && (
          <Box sx={lockNoteIconStyles}>
            <LockIcon size={12} />
          </Box>
        )}
        <Typography variant="regular12">{note}</Typography>
      </Box>
    )}
  </Box>
);

OriginatorColumn.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  note: PropTypes.string,
  isLockedNote: PropTypes.bool,
};

const OriginatorCompCard = ({ originator }) => {
  const { loanOfficer, broker, creditor, compensation } = originator;

  const columns = [
    {
      key: 'loanOfficer',
      label: 'Loan Officer',
      value: loanOfficer?.name,
      note: loanOfficer?.nmls ? `NMLS ${loanOfficer.nmls}` : null,
    },
    {
      key: 'broker',
      label: 'Broker',
      value: broker?.name,
      note: [broker?.nmls ? `NMLS ${broker.nmls}` : null, broker?.license]
        .filter(Boolean)
        .join(' · '),
    },
    {
      key: 'creditor',
      label: 'Creditor',
      value: creditor?.name,
      note: creditor?.note,
    },
    {
      key: 'compensation',
      label: 'Compensation',
      value: compensation?.label,
      note: compensation?.note,
      isLockedNote: !!compensation?.note,
    },
  ];

  return (
    <Box data-testid={FeesStepTestIds.ORIGINATOR_CARD} sx={cardStyles}>
      <Box sx={headerStyles}>
        <Box sx={headerIconStyles}>
          <LockIcon size={14} />
        </Box>
        <Typography sx={headerTitleStyles} variant="bold12">
          Originator & Compensation · Read-only
        </Typography>
      </Box>
      <Box sx={columnsStyles}>
        {columns.map(({ key, ...column }) => (
          <OriginatorColumn key={key} {...column} />
        ))}
      </Box>
    </Box>
  );
};

OriginatorCompCard.propTypes = {
  originator: PropTypes.shape({
    loanOfficer: PropTypes.shape({
      name: PropTypes.string,
      nmls: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    broker: PropTypes.shape({
      name: PropTypes.string,
      nmls: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      license: PropTypes.string,
    }),
    creditor: PropTypes.shape({ name: PropTypes.string, note: PropTypes.string }),
    compensation: PropTypes.shape({ label: PropTypes.string, note: PropTypes.string }),
  }).isRequired,
};

export default memo(OriginatorCompCard);
